import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { CaptureResponse, CaptureType } from "@idea-studio/shared";
import { postCapture } from "../api/client";

interface Props {
  type: CaptureType;
  onCancel: () => void;
  onSuccess: (capture: CaptureResponse) => void;
}

const LABELS: Record<CaptureType, { title: string; placeholder: string }> = {
  problem: { title: "Probleem", placeholder: "Welke vastloper of frustratie?" },
  idea: { title: "Idee", placeholder: "Wat is het idee? Eén of twee zinnen." },
  improvement: { title: "Verbetering", placeholder: "Wat kan beter aan de studio of app?" },
};

// Phase machine: idle → saving → (success | error_write | error_audit).
// error_write = niets opgeslagen, retry mag. error_audit = capture is in
// state.captures (durable, triage pakt 'm op) maar audit-log mislukte; retry
// NIET, want dat maakt een duplicate via een nieuwe CAP-ID.
type Phase =
  | { kind: "idle" }
  | { kind: "saving" }
  | { kind: "error_write"; message: string }
  | { kind: "error_audit"; capture: CaptureResponse };

export function CaptureScreen({ type, onCancel, onSuccess }: Props) {
  const [text, setText] = useState("");
  const [phase, setPhase] = useState<Phase>({ kind: "idle" });
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const queryClient = useQueryClient();
  const labels = LABELS[type];

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const mutation = useMutation({
    mutationFn: () => postCapture({ type, text: text.trim() }),
    onMutate: () => setPhase({ kind: "saving" }),
    onSuccess: (capture) => {
      queryClient.invalidateQueries({ queryKey: ["summary"] });
      queryClient.invalidateQueries({ queryKey: ["decisions"] });
      if (capture.warnings.includes("audit_log_partial")) {
        setPhase({ kind: "error_audit", capture });
        return;
      }
      onSuccess(capture);
    },
    onError: (err) => {
      const message = err instanceof Error ? err.message : "onbekende fout";
      setPhase({ kind: "error_write", message });
    },
  });

  const canSubmit = text.trim().length > 0 && phase.kind !== "saving";
  const isAuditOnly = phase.kind === "error_audit";

  return (
    <div className="flex min-h-[78vh] flex-col">
      <div className="flex items-center justify-between border-b border-neutral-900 pb-4">
        <button
          type="button"
          onClick={onCancel}
          disabled={phase.kind === "saving"}
          className="text-[14px] text-neutral-400 transition-colors active:text-neutral-200 disabled:opacity-40"
        >
          Annuleren
        </button>
        <span className="text-[10px] font-medium uppercase tracking-[0.22em] text-neutral-500">
          {labels.title}
        </span>
        <span className="w-16" aria-hidden="true" />
      </div>

      <textarea
        ref={textareaRef}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={labels.placeholder}
        rows={10}
        maxLength={2000}
        disabled={isAuditOnly}
        className="mt-6 flex-1 resize-none rounded-xl border border-neutral-800 bg-neutral-900/40 p-4 text-[15px] leading-relaxed text-neutral-100 placeholder:text-neutral-600 focus:border-neutral-600 focus:outline-none disabled:opacity-60"
      />

      <div className="mt-3 flex items-center justify-between font-mono text-[11px] text-neutral-600">
        <span>{text.length} / 2000</span>
        {phase.kind === "error_write" && (
          <span className="text-rose-300">opslaan mislukt — niets bewaard</span>
        )}
      </div>

      {phase.kind === "error_audit" ? (
        <AuditPartialBanner
          capture={phase.capture}
          onContinue={() => onSuccess(phase.capture)}
        />
      ) : (
        <button
          type="button"
          onClick={() => mutation.mutate()}
          disabled={!canSubmit}
          className="mt-6 min-h-[52px] rounded-xl bg-neutral-100 px-6 text-[15px] font-medium text-neutral-950 transition-colors active:bg-neutral-300 disabled:cursor-not-allowed disabled:bg-neutral-800 disabled:text-neutral-600"
        >
          {phase.kind === "saving"
            ? "Opslaan…"
            : phase.kind === "error_write"
              ? "Opnieuw proberen"
              : "Opslaan"}
        </button>
      )}

      {phase.kind === "error_write" && (
        <p className="mt-3 font-mono text-[10.5px] leading-relaxed text-neutral-600">
          {/* Detail-message verborgen in mono; klein om de UI niet te overspoelen */}
          {(() => {
            const m = phase.kind === "error_write" ? phase.message : "";
            return m.length > 220 ? m.slice(0, 220) + "…" : m;
          })()}
        </p>
      )}
    </div>
  );
}

function AuditPartialBanner({
  capture,
  onContinue,
}: {
  capture: CaptureResponse;
  onContinue: () => void;
}) {
  return (
    <div className="mt-6 space-y-4 rounded-xl border border-amber-900/50 bg-amber-950/20 p-4">
      <div className="space-y-1.5">
        <div className="text-[10px] font-medium uppercase tracking-[0.22em] text-amber-300/80">
          Opgeslagen · audit-log incompleet
        </div>
        <p className="text-[13.5px] leading-relaxed text-amber-100/90">
          <span className="font-mono">{capture.capture_id}</span> staat in de
          state en wordt door triage opgepakt. De JSONL-audit-log werd niet
          meegeschreven.
        </p>
        <p className="text-[12px] leading-relaxed text-amber-200/60">
          Niet opnieuw indienen — dat maakt een duplicate met een ander ID.
          Repareer later handmatig met <span className="font-mono">git</span> als
          je de audit-log wil completeren.
        </p>
      </div>
      <button
        type="button"
        onClick={onContinue}
        className="min-h-[44px] w-full rounded-lg border border-amber-700/50 px-4 text-[13px] font-medium text-amber-100 transition-colors active:bg-amber-900/40"
      >
        Doorgaan
      </button>
    </div>
  );
}
